import React from 'react';
import { useAssets } from '../hooks/useInvestQueries';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import AppLayout from '../components/layouts/AppLayout';
import ResponsiveNavbar from '../components/ResponsiveNavbar';
import { TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function AssetsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: assets = [], isLoading: loading, error } = useAssets();
  
  if (error) {
    toast.error('Failed to load assets');
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Even if logout fails, still navigate to login
      navigate('/login');
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="assets-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={false} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Investment Opportunities</h1>
            <p className="text-[#94A3B8] text-sm sm:text-base">Browse exclusive assets and commit to your next investment</p>
          </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : assets.length === 0 ? (
          <div className="premium-card p-12 text-center">
            <p className="text-[#64748B] text-lg">No assets available at the moment. Check back soon!</p>
          </div>
        ) : (
          <div className="asset-grid" data-testid="assets-list">
            {assets.map(asset => {
              const remaining = asset.percent_remaining || 0;
              const statusColor = remaining > 50 ? 'text-[#10B981]' : remaining > 25 ? 'text-[#F59E0B]' : 'text-[#EF4444]';
              
              return (
                <div key={asset.asset_id} onClick={() => navigate(`/assets/${asset.asset_id}`)} className="premium-card p-6 hover:scale-[1.02] transition-all cursor-pointer" data-testid={`asset-${asset.asset_id}`}>
                  {asset.images && asset.images.length > 0 && (
                    <div className="w-full h-48 rounded-lg mb-4 overflow-hidden">
                      <img src={asset.images[0]} alt={asset.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-['Outfit'] text-2xl font-bold">{asset.name}</h3>
                    {remaining < 20 && <span className="px-2 py-1 bg-[#EF4444]/20 text-[#EF4444] text-xs rounded-full border border-[#EF4444]/30">Almost Full</span>}
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">{asset.description}</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8] text-sm">Total Value</span>
                      <span className="font-['JetBrains_Mono'] font-semibold">${asset.total_value.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8] text-sm">Min Investment</span>
                      <span className="font-['JetBrains_Mono'] font-semibold">{asset.min_percentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8] text-sm">Available</span>
                      <span className={`font-['JetBrains_Mono'] font-semibold ${statusColor}`}>{remaining.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2 mb-4">
                    <div className="h-full bg-gradient-to-r from-[#10B981] to-[#D4AF37] rounded-full transition-all" style={{ width: `${100 - remaining}%` }}></div>
                  </div>
                  <button className="w-full py-2.5 sm:py-3 text-sm sm:text-base bg-[#D4AF37] text-[#0A1128] font-bold rounded hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all" data-testid={`view-asset-btn-${asset.asset_id}`}>
                    View Details
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  );
}
